/* eslint-disable */
import { Button } from '@blueprintjs/core';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { getActiveBrand } from '../../../store/selector/brand.selector';
import {
    getAdFromChat,
    getAdFromCreateTab,
    getProjectCreationStatus,
    getTemplateFromProject,
    getTemplateLoadingState,
} from '../../../store/selector/editor.selector';
import { getDesignElements } from '../../../store/selector/brand-elements.selector';
import { useCreateOrUpdateProjectMutation } from '../../../store/api/editor.api';
import { useAddCampaignAdVersionMutation } from '../../../store/api/campaign-ads.api';
import { compareTemplates } from '../../../utils/TemplateUtils';
import {
    SetRefetchCampaignAds,
    SetRefetchProjects,
    SetTemplateJSONForAdFromCreateTab,
    SetTemplateJSONForTemplateFromProject,
} from '../../../store/slice/editor.slice';
import { addCorrectFontsInTemplate } from '../../../utils/template.util';
import { displaySuccessToast } from '../../../utils/ToastUtils';

export const PolotnoQuickSaveButton = observer(({ store }: { store: any }) => {
    const dispatch = useAppDispatch();
    const activeBrand = useAppSelector(getActiveBrand);
    const templateFromProject = useAppSelector(getTemplateFromProject);
    const adFromChat = useAppSelector(getAdFromChat);
    const adFromCreateTab = useAppSelector(getAdFromCreateTab);
    const projectCreationStatus = useAppSelector(getProjectCreationStatus);
    const templateLoadingState = useAppSelector(getTemplateLoadingState);
    const designElementsData = useAppSelector(getDesignElements);
    const [createOrUpdateProject, { isLoading: updateProjectLoading }] =
        useCreateOrUpdateProjectMutation();
    const [addCampaignAdVersion, { isLoading: updateCampaignAdLoading }] =
        useAddCampaignAdVersionMutation();

    const [runAutoSaveCounter, setRunAutoSaveCounter] = useState(1);
    const [timeoutTime, setTimeoutTime] = useState(500);

    const saveTemplateFromProject = async (
        projectJSON: any,
        counter: number,
        showMessage: boolean,
    ) => {
        try {
            if (!templateFromProject || !activeBrand) return;
            if (!compareTemplates(projectJSON, templateFromProject.templateJSON)) {
                if (counter === 1) {
                    return;
                }
                dispatch(SetRefetchProjects(true));
                dispatch(SetTemplateJSONForTemplateFromProject(projectJSON));
                await createOrUpdateProject({
                    id: templateFromProject.id,
                    json: projectJSON,
                    brandId: activeBrand.id,
                });
            } else if (showMessage) {
                displaySuccessToast('Project already saved');
            }
        } catch (e) {}
    };

    const saveAdFromCreateTab = async (projectJSON: any, counter: number, showMessage: boolean) => {
        try {
            if (!adFromCreateTab) return;
            if (!compareTemplates(projectJSON, adFromCreateTab.templateJSON)) {
                if (counter === 1) {
                    return;
                }
                dispatch(SetRefetchCampaignAds(true));
                dispatch(SetTemplateJSONForAdFromCreateTab(projectJSON));
                await addCampaignAdVersion({
                    campaignAssetId: adFromCreateTab.campaignAdId,
                    json: projectJSON,
                });
            } else if (showMessage) {
                displaySuccessToast('Ad already saved');
            }
        } catch (e) {}
    };

    const handleSaveOnClick = async () => {
        if (projectCreationStatus || templateLoadingState) return;
        if (!adFromChat && !adFromCreateTab && !templateFromProject) return;
        const storeJSON = store.toJSON();
        const projectJSON = addCorrectFontsInTemplate(storeJSON, designElementsData);
        if (adFromChat) {
            // TODO Rahul: handle saving. Also update preview image
            return;
        }
        if (adFromCreateTab) {
            await saveAdFromCreateTab(projectJSON, 2, true);
            return;
        }
        if (templateFromProject) {
            await saveTemplateFromProject(projectJSON, 2, true);
            return;
        }
    };

    const handleAutoSave = async (counter: number) => {
        if (projectCreationStatus || templateLoadingState) return;
        if (!adFromCreateTab && !templateFromProject) return;
        const storeJSON = store.toJSON();
        const projectJSON = addCorrectFontsInTemplate(storeJSON, designElementsData);

        if (adFromCreateTab) {
            await saveAdFromCreateTab(projectJSON, counter, false);
            return;
        }
        if (templateFromProject) {
            await saveTemplateFromProject(projectJSON, counter, false);
            return;
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const counter = runAutoSaveCounter;
            if (timeoutTime === 500) {
                setTimeoutTime(5000);
            }
            setRunAutoSaveCounter((prevCounter) => {
                if (prevCounter === 100) return 1;
                return prevCounter + 1;
            });
            handleAutoSave(counter);
        }, timeoutTime);
        return () => clearTimeout(timeoutId);
    }, [runAutoSaveCounter]);

    return (
        <>
            {!adFromChat && !adFromCreateTab && !templateFromProject ? (
                <></>
            ) : (
                <OverlayTrigger
                    placement="bottom"
                    delay={{ show: 250, hide: 400 }}
                    overlay={<Tooltip>Save</Tooltip>}
                >
                    <Button
                        icon="floppy-disk"
                        intent="primary"
                        loading={updateProjectLoading || updateCampaignAdLoading}
                        onClick={handleSaveOnClick}
                        className="d-flex align-item-center justify-content-center me-2"
                    ></Button>
                </OverlayTrigger>
            )}
        </>
    );
});
